// Localização: frontend/src/pages/Agendamentos/AgendamentoCard.js (versão 0.0.4 - Com foto do profissional destacada)

import React from 'react';
import { 
    CalendarCheck, Clock, User, Pencil, Trash2, MapPin, CalendarCheck as CheckIcon, Users
} from 'lucide-react';
import PrimaryButton from '../../components/Common/PrimaryButton';
import { usePacienteList } from '../../hooks/usePacienteList';
import { getPacienteFotoUrl, getUsuarioFotoUrl } from '../../utils/imageUtils';
import { parseLocalDateTime } from '../../utils/formatters';

// Define a URL do avatar de fallback
const FALLBACK_AVATAR = '/images/higeia-avatar-shield.png';

// Mapeamento de Status para estilo
const statusStyles = {
    'Pendente': 'bg-yellow-100 text-yellow-800 border-yellow-300',
    'Em Andamento': 'bg-blue-100 text-blue-800 border-blue-300',
    'Concluído': 'bg-green-100 text-green-800 border-green-300',
    'Cancelado': 'bg-red-100 text-red-800 border-red-300',
};

// --------------------------------------------------------------------------------
// Componente para exibir um único agendamento (Card seguindo padrão do PacienteCard)
// --------------------------------------------------------------------------------
const AgendamentoCard = ({ agendamento, onEdit, onDelete, onUpdateStatus, onViewDetails }) => {
    const { pacientes } = usePacienteList();
    
    // Encontrar paciente relacionado
    const paciente = pacientes.find(p => p.id === agendamento.paciente_id);
    const nomePaciente = agendamento.paciente_nome || paciente?.nome_completo || 'Não Atribuído';
    
    // Calcula a duração ou apenas exibe o tipo
    // CORREÇÃO: Usar parseLocalDateTime para garantir que a hora seja interpretada corretamente
    // O backend envia datas como UTC (com Z), mas o banco salva sem timezone.
    // A função parseLocalDateTime extrai os componentes UTC e cria como hora local,
    // preservando a hora exata que estava no banco.
    const dataInicio = parseLocalDateTime(agendamento.data_hora_inicio);
    const dataFim = agendamento.data_hora_fim ? parseLocalDateTime(agendamento.data_hora_fim) : null;
    
    // Verificar se as datas são válidas
    if (!dataInicio) {
        console.warn('[AgendamentoCard] Data de início inválida:', agendamento.data_hora_inicio);
    }
    
    // Formata a data e hora
    const formatTime = (date) => {
        if (!date || isNaN(date.getTime())) return 'Hora inválida';
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };
    
    const formattedDate = dataInicio && !isNaN(dataInicio.getTime())
        ? dataInicio.toLocaleDateString('pt-BR', { 
            weekday: 'short', day: '2-digit', month: 'long' 
        })
        : 'Data inválida';
    
    const formattedTime = dataFim && !isNaN(dataFim.getTime())
        ? `${formatTime(dataInicio)} - ${formatTime(dataFim)}`
        : formatTime(dataInicio);

    const statusClass = statusStyles[agendamento.status] || 'bg-gray-100 text-gray-800 border-gray-300';

    // Foto do profissional (que realizará o plantão)
    const profissionalFotoUrl = getUsuarioFotoUrl(agendamento.profissional_foto_url);
    const nomeProfissional = agendamento.profissional_nome || 'Não Atribuído';

    return (
        // Card seguindo o padrão do PacienteCard: header com foto + corpo (tamanho reduzido para mobile)
        <div 
            className="bg-white rounded-lg sm:rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200 hover:border-emerald-400 group cursor-pointer"
            onClick={() => onViewDetails && onViewDetails(agendamento)}
        >
            {/* Header do Card com Foto do Profissional (REDUZIDA) */}
            <div className="relative bg-gradient-to-br from-emerald-50 to-emerald-100 p-2 sm:p-3">
                <div className="flex items-center space-x-2 sm:space-x-3">
                    {/* Foto do Profissional (REDUZIDA) */}
                    <div className="flex-shrink-0">
                        <div className="relative">
                            <img 
                                className="h-12 w-12 sm:h-16 sm:w-16 rounded-full object-cover border-2 sm:border-3 border-white shadow-md group-hover:scale-105 transition-transform duration-300" 
                                src={profissionalFotoUrl} 
                                alt={`Foto de ${nomeProfissional}`} 
                                onError={(e) => {
                                    e.target.onerror = null; 
                                    e.target.src = FALLBACK_AVATAR; 
                                }}
                            />
                            {/* Status Badge (REDUZIDO) */}
                            <div className={`absolute -bottom-0.5 -right-0.5 sm:-bottom-1 sm:-right-1 h-4 w-4 sm:h-5 sm:w-5 rounded-full border border-white ${
                                agendamento.status === 'Concluído' ? 'bg-green-500' :
                                agendamento.status === 'Cancelado' ? 'bg-red-500' :
                                agendamento.status === 'Em Andamento' ? 'bg-blue-500' :
                                'bg-yellow-500'
                            }`} title={agendamento.status}></div>
                        </div>
                    </div>
                    
                    {/* Nome do Profissional e Tipo de Cuidado */}
                    <div className="flex-1 min-w-0">
                        <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate group-hover:text-emerald-700 transition-colors">
                            {nomeProfissional}
                        </h3>
                        <p className="text-xs text-gray-600 truncate mt-0.5">
                            {agendamento.tipo_cuidado || agendamento.tipo || 'Agendamento'}
                        </p>
                        <p className="text-xs text-emerald-600 font-medium truncate mt-0.5">
                            Plantão
                        </p>
                    </div>
                </div>
            </div>

            {/* Corpo do Card */}
            <div className="p-2 sm:p-3 space-y-2 sm:space-y-2.5">
                {/* Informações */}
                <div className="space-y-1.5 sm:space-y-2">
                    {/* Data */}
                    <div className="flex items-center text-xs sm:text-sm text-gray-600">
                        <CalendarCheck className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-emerald-600 flex-shrink-0" />
                        <span className="truncate">
                            <span className="font-semibold">Data:</span> {formattedDate}
                        </span>
                    </div>

                    {/* Horário */}
                    <div className="flex items-center text-xs sm:text-sm text-gray-600">
                        <Clock className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-emerald-600 flex-shrink-0" />
                        <span className="truncate">
                            <span className="font-semibold">Horário:</span> {formattedTime}
                        </span>
                    </div>

                    {/* Paciente */}
                    <div className="flex items-center text-xs sm:text-sm text-gray-600">
                        <Users className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 text-emerald-600 flex-shrink-0" />
                        <div className="flex items-center space-x-1.5 sm:space-x-2 flex-1 min-w-0">
                            {/* Foto do Paciente (REDUZIDA) */}
                            <div className="flex-shrink-0">
                                <img 
                                    className="h-6 w-6 sm:h-7 sm:w-7 rounded-full object-cover border border-gray-200" 
                                    src={getPacienteFotoUrl(paciente?.fotoURL)} 
                                    alt={`Foto de ${nomePaciente}`} 
                                    onError={(e) => {
                                        e.target.onerror = null; 
                                        e.target.src = FALLBACK_AVATAR; 
                                    }}
                                />
                            </div>
                            <span className="truncate">
                                <span className="font-semibold">Paciente:</span> {nomePaciente}
                            </span>
                        </div>
                    </div>

                    {/* Status Badge */}
                    <div className="flex items-center justify-between pt-0.5">
                        <span className={`px-2 sm:px-2.5 py-0.5 sm:py-1 inline-flex text-xs font-semibold rounded-full ${statusClass}`}>
                            {agendamento.status}
                        </span>
                    </div>

                    {/* Descrição */}
                    {agendamento.descricao && (
                        <p className="text-xs text-gray-500 border-l-2 pl-2 sm:pl-3 border-emerald-300 italic mt-1.5 line-clamp-2">
                            {agendamento.descricao}
                        </p>
                    )}
                </div>

                {/* Ações */}
                <div className="flex items-center justify-between pt-2 sm:pt-2.5 border-t border-gray-200">
                    <div className="flex items-center space-x-1 sm:space-x-2">
                        {/* Botão Check-in/Iniciar ou Concluir */}
                        {(agendamento.status === 'Pendente' || agendamento.status === 'Em Andamento') && (
                            <PrimaryButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onUpdateStatus(agendamento.id, agendamento.status === 'Pendente' ? 'Em Andamento' : 'Concluído');
                                }}
                                title={agendamento.status === 'Pendente' ? "Iniciar Agendamento" : "Concluir Agendamento"}
                                className={`!p-1.5 sm:!p-2 !rounded-md sm:!rounded-lg ${
                                    agendamento.status === 'Pendente' 
                                        ? '!bg-green-600 hover:!bg-green-700 !text-white' 
                                        : '!bg-teal-600 hover:!bg-teal-700 !text-white'
                                }`}
                            >
                                {agendamento.status === 'Pendente' ? (
                                    <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
                                ) : (
                                    <CheckIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                                )}
                            </PrimaryButton>
                        )}
                    </div>

                    <div className="flex items-center space-x-1 sm:space-x-2">
                        {/* Botão Editar */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit(agendamento);
                            }}
                            className="p-1.5 sm:p-2 text-emerald-600 hover:bg-emerald-50 rounded-md sm:rounded-lg transition-colors duration-200"
                            title="Editar"
                        >
                            <Pencil className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        
                        {/* Botão Excluir */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(agendamento.id);
                            }}
                            className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-md sm:rounded-lg transition-colors duration-200"
                            title="Excluir"
                        >
                            <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AgendamentoCard;
